import { fetchAllAdminUsers } from '@actions/adminUsers';
import { fetchChallengeGroup } from '@actions/challengeGroups';
import {
    editChallenge,
    EditChallengeRequest,
    fetchChallengeCalendarAdminOptions,
    fetchChallengeLead,
    fetchSingleChallenge,
} from '@actions/challenges';
import { fetchAllChallengeTags } from '@actions/challengeTags';
import { fetchAllBusinessTags } from '@actions/challengeTags/fetchAllBusinessTags';
import {
    getCalendarAssessors,
    getCalendarPitchPresentationAdmins,
    getCalendarWorkshopAdmins,
    getChallenge,
    getChallengeLeads,
    getChallengesFetchError,
    getChallengesIsFetching,
    getChallengesIsPosting,
    getChallengesPostError,
    getChallengesPostSuccess,
} from '@selectors/challenges';
import { getBusinessTags, getChallengeTags } from '@selectors/challengeTags';
import dayjs from 'dayjs';
import useForm from '@components/form/hooks/useForm';
import usePrevious from 'lib/src/hooks/usePrevious';
import { DropdownOption } from 'lib/src/types/shared/DropdownOption';
import { useEffect, useMemo } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { AdminUser } from 'src/types/shared/AdminUser';
import { Tag } from 'src/types/shared/Tags';
import { ChallengeDescriptionSection, SelectUser } from 'src/types/shared/Challenge';
import { RootState } from '@reducers/index';
import { ChallengeCalendarAdminOption } from 'src/types/shared/User';

const useEditChallenge = (challengeID: number) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const challenge = useSelector((state: RootState) => getChallenge(state, +challengeID));

    const postSuccess = useSelector(getChallengesPostSuccess);
    const isPosting = useSelector(getChallengesIsPosting);
    const error = useSelector(getChallengesPostError);

    const isFetching = useSelector(getChallengesIsFetching);
    const fetchError = useSelector(getChallengesFetchError);

    const challengeTags = useSelector(getChallengeTags);
    const businessTags = useSelector(getBusinessTags);
    const leadOptions = useSelector(getChallengeLeads);
    const workshopAdmins = useSelector(getCalendarWorkshopAdmins) || {};
    const assessors = useSelector(getCalendarAssessors) || {};
    const pitchPresentationAdmins = useSelector(getCalendarPitchPresentationAdmins) || {};
    // todo limit to admins with challenge group permissions
    const prevSite = usePrevious(challenge.site);

    const initialData = useMemo(() => {
        const {
            challengeGroupID,
            name,
            startDate,
            endDate,
            gracePeriod,
            background,
            imageS3Key,
            partnerLogoS3Key,
            partnerSecondLogoS3Key,
            partnerThirdLogoS3Key,
            summaryImage1S3Key,
            summaryImage2S3Key,
            responseDate,
            challengeLeadID,
            site,
            descriptionSections,
            awardDescription,
            workshopDate,
            workshopAdminIDs,
            assessmentStartDate,
            assessorIDs,
            pitchPresentationDate,
            pitchPresentationAdminIDs,
        } = challenge;

        const tagIDValues = Array.isArray(challenge.challengeTags)
            ? challenge.challengeTags.map(getTagValue)
            : [];
        const businessTagIDValues = Array.isArray(challenge.businessTags)
            ? challenge.businessTags.map(getTagValue)
            : [];

        return {
            challengeGroupID,
            name,
            startDate: dayjs(startDate ? startDate : undefined).toDate(),
            endDate: dayjs(endDate).toDate(),
            gracePeriod,
            background,
            imageS3Key: !!imageS3Key ? [imageS3Key] : [],
            partnerLogoS3Key: !!partnerLogoS3Key ? [partnerLogoS3Key] : [],
            partnerSecondLogoS3Key: !!partnerSecondLogoS3Key ? [partnerSecondLogoS3Key] : [],
            partnerThirdLogoS3Key: !!partnerThirdLogoS3Key ? [partnerThirdLogoS3Key] : [],
            summaryImage1S3Key: !!summaryImage1S3Key ? [summaryImage1S3Key] : [],
            summaryImage2S3Key: !!summaryImage2S3Key ? [summaryImage2S3Key] : [],
            responseDate: dayjs(responseDate).toDate(),
            challengeLeadID,
            tagIDs: tagIDValues,
            businessTagIDs: businessTagIDValues,
            site,
            descriptionSections: descriptionSections || [],
            awardDescription: awardDescription || '',
            workshopDate: dayjs(workshopDate).toDate(),
            workshopAdminIDs,
            assessmentStartDate: dayjs(assessmentStartDate).toDate(),
            assessorIDs,
            pitchPresentationDate: dayjs(pitchPresentationDate).toDate(),
            pitchPresentationAdminIDs,
        };
    }, [challenge]);

    const { id, challengeGroupID } = challenge;

    useEffect(() => {
        if (
            !!challenge.site &&
            (challenge.site !== prevSite || Object.values(assessors).length < 1) &&
            challengeGroupID
        ) {
            dispatch(fetchChallengeCalendarAdminOptions(challengeGroupID));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [challenge.site, dispatch, challengeGroupID]);

    const [formState, handleChange, resetData] = useForm<FormData>(initialData);

    const prevProps = usePrevious({ postSuccess, formState, challenge });
    const handleSubmit = () => {
        const request: EditChallengeRequest = {
            ...formState,
            responseDate: dayjs(formState.responseDate).format('YYYY-MM-DD'),
            startDate: dayjs(formState.startDate).format('YYYY-MM-DD'),
            endDate: dayjs(formState.endDate).format('YYYY-MM-DD'),
            workshopDate: dayjs(formState.workshopDate).format('YYYY-MM-DD'),
            assessmentStartDate: dayjs(formState.assessmentStartDate).format('YYYY-MM-DD'),
            pitchPresentationDate: dayjs(formState.pitchPresentationDate).format('YYYY-MM-DD'),
            imageS3Key: formState.imageS3Key[0] || null,
            partnerLogoS3Key: formState.partnerLogoS3Key[0] || null,
            partnerSecondLogoS3Key: formState.partnerSecondLogoS3Key[0] || null,
            partnerThirdLogoS3Key: formState.partnerThirdLogoS3Key[0] || null,
            summaryImage1S3Key: formState.summaryImage1S3Key[0] || null,
            summaryImage2S3Key: formState.summaryImage2S3Key[0] || null,
        };

        dispatch(editChallenge(id, request));
    };

    useEffect(() => {
        if (Object.keys(prevProps.challenge).length !== Object.keys(challenge).length) {
            resetData(initialData);
        }
    }, [prevProps.challenge, challenge, initialData, resetData]);

    useEffect(() => {
        if (!!challengeID) {
            batch(() => {
                dispatch(fetchSingleChallenge(challengeID));
                dispatch(fetchChallengeLead(challengeID));
            });
        }
        if (!!challengeGroupID) dispatch(fetchChallengeGroup(challengeGroupID));
    }, [dispatch, challengeID, challengeGroupID]);

    useEffect(() => {
        batch(() => {
            dispatch(fetchAllChallengeTags());
            dispatch(fetchAllBusinessTags());
            dispatch(fetchAllAdminUsers());
        });
    }, [dispatch]); // Always fetch these on mount

    useEffect(() => {
        if (postSuccess && !prevProps.postSuccess) {
            history.push(`/challenge-groups/${challengeGroupID}/challenges/${id}`);
        }
    }, [postSuccess, prevProps.postSuccess, history, challengeGroupID, id]);

    const tagOptions = Object.values(challengeTags).map(getTagOption);
    const businessTagOptions = Object.values(businessTags).map(getTagOption);
    const adminOptions = Object.values(leadOptions).map(getUserOption);
    const assessorOptions = Object.values(assessors)
        .sort((a, b) => a.lastName.localeCompare(b.lastName))
        .map(getUserOption);
    const workshopAdminOptions = Object.values(workshopAdmins)
        .sort((a, b) => a.lastName.localeCompare(b.lastName))
        .map(getUserOption);
    const pitchPresentationAdminOptions = Object.values(pitchPresentationAdmins)
        .sort((a, b) => a.lastName.localeCompare(b.lastName))
        .map(getUserOption);

    return {
        formState,
        handleChange,
        handleSubmit,
        isPosting,
        error,
        tagOptions,
        businessTagOptions,
        adminOptions,
        workshopAdminOptions,
        assessorOptions,
        pitchPresentationAdminOptions,
        challenge,
        isFetching,
        fetchError,
    };
};

const getTagOption = (tag: Tag): DropdownOption<number> => ({ value: tag.id, label: tag.name });
const getTagValue = (tag: Tag): number => tag.id;
const getUserOption = (
    user: AdminUser | SelectUser | ChallengeCalendarAdminOption,
): DropdownOption<number> => ({
    value: user.id,
    label: user.firstName + ' ' + user.lastName,
});

interface FormData {
    challengeGroupID: number;
    name: string;
    startDate: Date | string;
    endDate: Date | string;
    gracePeriod: number;
    background: string;
    imageS3Key: string[];
    partnerLogoS3Key: string[];
    partnerSecondLogoS3Key: string[];
    partnerThirdLogoS3Key: string[];
    summaryImage1S3Key: string[];
    summaryImage2S3Key: string[];
    responseDate: Date | string;
    challengeLeadID: number | null;
    tagIDs: number[];
    businessTagIDs: number[];
    site: number;
    descriptionSections: ChallengeDescriptionSection[];
    awardDescription: string;
    workshopDate: Date | string;
    workshopAdminIDs: number[];
    assessmentStartDate: Date | string;
    assessorIDs: number[];
    pitchPresentationDate: Date | string;
    pitchPresentationAdminIDs: number[];
}
export default useEditChallenge;
